import {FadeInView} from '@/components/basic/animations';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FlatList, ListRenderItemInfo, RefreshControl, View} from 'react-native';
import theme from '@style';
import {useInnerStyle} from './casino.hooks';
import {
  NewGameListItem,
  NewGameListParams,
  postNewGameList,
} from '@/common-pages/casino/casino.service';
import globalStore from '@/services/global.state';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import {NoMoreData} from '@basicComponents/default-page';
import Wallet from '@/common-pages/wallet';
// import {BasicObject, NavigatorScreenProps} from '@/types';
import {useFocusEffect} from '@react-navigation/native';
import NoData from '@/components/basic/error-pages/no-data';
import {toGame} from '@/common-pages/game-navigate';
import {useTranslation} from 'react-i18next';
import CasinoTabs from './casino-tabs';
import {goBack, groupArrayByLength} from '@/utils';
import {Image} from 'antd-mobile';
import HotList from '@/common-pages/hot-list/index_next';
import CasinoSelect from '@/pages/casino/casino-select';
import {useGameType} from '@/pages/casino/useGameType';
import {getGamePicUrl, GamePicType} from '@/components/utils/utils';

const Casino = () => {
  const [isInit, setInit] = useState(false);
  const {i18n} = useTranslation();
  // const {route} = props;
  const homeCategoryId = 2;
  const {
    size: {screenWidth, screenHeight},
  } = useInnerStyle();
  const [gameList, setGameList] = useState<NewGameListItem[]>([]);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const pageNo = useRef(1);
  const totalPage = useRef(1);
  const firstScreenRef = useRef<FlatList<NewGameListItem[]>>(null);
  const [provider, setProvider] = useState<string>('');
  const [sort, setSort] = useState<string>('');
  // const [contentHeight, setContentHeight] = useState(0);
  const {gameTypes, gameType, setGameType} = useGameType();

  const pairedGameList = useMemo(() => {
    return groupArrayByLength(gameList, 3);
  }, [gameList]);

  const handleChangeProvider = (value: string) => {
    setProvider(value);
  };

  const handleChangeSort = (value: string) => {
    setSort(value);
  };
  const handleFocusEffect = useCallback(() => {
    const sub = globalStore.tokenSubject.subscribe(token => {
      if (token) {
        globalStore.amountCheckOut.next({});
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);
  useFocusEffect(handleFocusEffect);

  const getList = useCallback(
    (showloading = true) => {
      showloading && globalStore.globalLoading.next(true);
      const params: NewGameListParams = {
        l1CategoryId: homeCategoryId,
        pageNo: pageNo.current,
        pageSize: 21,
        l2CategoryId: ['Lobby', 'hotGames'].includes(gameType) ? '' : gameType,
        providerId: provider,
      };
      if (sort) {
        params.orderByField = 'name';
        params.isAsc = sort === 'A-Z';
      }
      return postNewGameList(params)
        .then(game => {
          if (pageNo.current <= 1) {
            setGameList(game.content || []);
          } else {
            setGameList(_gameList => [..._gameList, ...(game.content || [])]);
          }

          totalPage.current = game.totalPages;
        })
        .finally(() => {
          globalStore.globalLoading.next(false);
          setRefreshing(false);
        });
    },
    [homeCategoryId, gameType, provider, sort],
  );

  const refreshNextPage = () => {
    if (totalPage.current === 1) {
      return;
    }
    pageNo.current++;
    if (pageNo.current > totalPage.current) {
      return;
    }
    globalStore.globalLoading.next(true);
    getList(true);
  };

  const computedSize = (num: number) => (num * screenWidth) / 375;
  const size = {
    computedS: computedSize(theme.paddingSize.s),
    computedL: computedSize(theme.paddingSize.l),
  };

  const renderItem = ({
    item: subGameList,
    index,
  }: ListRenderItemInfo<NewGameListItem[]>) => {
    // const isLastItemOfFirstScreen =
    //   (index + 1) * (screenWidth * 1.39 + size.computedS) > screenHeight - 200;
    return (
      <>
        <div className="grid grid-cols-3 gap-2 px-3" key={index}>
          {subGameList.map(item => (
            <div
              key={item.id}
              className="h-0 pb-[139%] relative rounded-lg overflow-hidden"
              onClick={() => toGame(item)}>
              <div className="absolute size-full">
                <Image
                  src={getGamePicUrl(
                    GamePicType.V_RECTANGLE,
                    item.source,
                    item.tripartiteUniqueness,
                  )}
                  className="size-full"
                  fit="fill"
                />
              </div>
            </div>
          ))}
        </div>
        {((pairedGameList?.length <= 2 &&
          index === pairedGameList.length - 1) ||
          index === 2) &&
          gameType === 'Lobby' && (
            <HotList
              type="CASINO"
              limit={3}
              wrapperClassName={data => (data && data.length > 0 ? 'mt-2' : '')}
            />
          )}
      </>
    );
  };

  useEffect(() => {
    if (!isInit) {
      return;
    }
    pageNo.current = 1;
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType, provider, sort, isInit]);
  useEffect(() => {
    if (gameTypes.length > 0 && gameType && !isInit) {
      setInit(true);
    }
  }, [gameType, isInit, gameTypes, provider]);

  return (
    <FadeInView style={[{height: screenHeight}, theme.background.lightGrey]}>
      <DetailNavTitle
        hideAmount
        hideServer
        title={i18n.t('home.title.casino')}
        themeType="dark"
        onBack={() => {
          goBack();
        }}
        rightNode={<Wallet theme="dark" />}
      />
      <CasinoTabs
        tabOptions={gameTypes}
        value={gameType}
        onChange={setGameType}
      />
      <CasinoSelect
        handleChangeProvider={handleChangeProvider}
        handleChangeSort={handleChangeSort}
        type="casino"
      />

      <FlatList
        ref={firstScreenRef}
        style={[theme.flex.flex1]}
        data={pairedGameList}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              getList(false);
            }}
          />
        }
        ListFooterComponent={
          gameList.length > 0 && pageNo.current >= totalPage.current ? (
            <NoMoreData />
          ) : undefined
        }
        contentContainerStyle={{
          gap: size.computedS,
          // paddingVertical: size.computedL,
        }}
        onEndReached={refreshNextPage}
        ListEmptyComponent={
          <View
            style={[theme.padding.xxl, theme.flex.center, theme.fill.fillW]}>
            <NoData />
            {/* {contentHeight < screenHeight && (
              <HotList type="CASINO" limit={3} />
            )} */}
          </View>
        }
        // onLayout={event => setContentHeight(event.nativeEvent.layout.height)}
      />
    </FadeInView>
  );
};

export default Casino;
